import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, Download, User, Mail, Phone, Award } from 'lucide-react';
import { getRiskProfile, getPublicShare } from '../utils/api';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { RUBICON_LOGO_BASE64 } from '../utils/pdfAssets';


const ViewRiskProfile = ({ publicToken = null }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetcher = publicToken
      ? getPublicShare(publicToken).then((r) => r.data)
      : getRiskProfile(id);
    fetcher
      .then((data) => { if (data) setProfile(data); })
      .catch(() => {
        if (!publicToken) {
          alert('Risk profile not found');
          navigate('/admin/risk-profiling');
        }
      })
      .finally(() => setLoading(false));
  }, [id, publicToken, navigate]);

  const getRiskColor = (riskProfile) => {
    switch (riskProfile) {
      case 'Conservative':
        return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'Moderate':
        return 'bg-amber-100 text-amber-700 border-amber-300';
      case 'Aggressive':
        return 'bg-red-100 text-red-700 border-red-300';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const downloadPDF = () => {
    if (!profile) return;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 15;

    // Add page footer function
    const addFooter = (pageNum) => {
      const footerY = pageHeight - 25;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(100, 100, 100);
      
      doc.text('6A Dr.Rajendra Prasad Sarani, 4th Floor, R.No-20, Kolkata-700001', pageWidth / 2, footerY, { align: 'center' });
      doc.text('Email: communications@rubiconfinservices.com | Website: www.rubiconfinservices.com | Tel: +91-33-40442577', pageWidth / 2, footerY + 4, { align: 'center' });
      
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.text('"If you think about the long term, you can take good life decisions which you won\'t regret later"', pageWidth / 2, footerY + 9, { align: 'center' });
      
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.text(`Page ${pageNum}`, pageWidth - 20, pageHeight - 10, { align: 'right' });
      
      doc.setTextColor(0, 0, 0);
    };

    // Add logo
    try {
      // Logo dimensions: 300x288 (aspect ratio 1.042)
      const logoWidth = 50;
      const logoHeight = 48; // Maintains aspect ratio
      doc.addImage(RUBICON_LOGO_BASE64, 'JPEG', (pageWidth - logoWidth) / 2, yPos, logoWidth, logoHeight);
      yPos += 52;
    } catch (error) {
      console.error('Error adding logo to PDF:', error);
      yPos += 20;
    }

    // Company name and registration
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(14);
    doc.setTextColor(30, 58, 138);
    doc.text('RUBICON FINSERVICES', pageWidth / 2, yPos, { align: 'center' });
    yPos += 6;

    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text('AMFI Registered Mutual Fund Distributor', pageWidth / 2, yPos, { align: 'center' });
    yPos += 4;
    doc.setFontSize(8);
    doc.text('ARN: 102867 | Initial Registration: 09-June-2015 | Valid till: 01-07-2027', pageWidth / 2, yPos, { align: 'center' });
    yPos += 3.5;
    doc.text('SIF: Valid till 15-03-2029  |  APRN: Valid till 17-10-2027  |  APMI: APMI-APRN01145', pageWidth / 2, yPos, { align: 'center' });
    yPos += 12;

    // Title
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(0, 0, 0);
    doc.text('INVESTOR RISK PROFILE ASSESSMENT', pageWidth / 2, yPos, { align: 'center' });
    yPos += 10;

    // Investor Information Box
    doc.setFillColor(240, 249, 255);
    doc.rect(15, yPos, pageWidth - 30, 35, 'F');
    doc.setDrawColor(59, 130, 246);
    doc.setLineWidth(0.5);
    doc.rect(15, yPos, pageWidth - 30, 35);
    
    yPos += 8;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Investor Information', 20, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Name: ${profile.investor_name}`, 20, yPos);
    yPos += 6;
    doc.text(`Email: ${profile.investor_email}`, 20, yPos);
    yPos += 6;
    doc.text(`Phone: ${profile.investor_phone}`, 20, yPos);
    doc.text(`Age: ${profile.age}`, pageWidth / 2 + 10, yPos);
    yPos += 12;

    // Risk Profile Result Box
    doc.setFillColor(255, 251, 235);
    if (profile.risk_profile === 'Conservative') {
      doc.setFillColor(219, 234, 254);
    } else if (profile.risk_profile === 'Moderate') {
      doc.setFillColor(254, 243, 199);
    } else if (profile.risk_profile === 'Aggressive') {
      doc.setFillColor(254, 226, 226);
    }
    
    doc.rect(15, yPos, pageWidth - 30, 20, 'F');
    doc.setDrawColor(59, 130, 246);
    doc.setLineWidth(1);
    doc.rect(15, yPos, pageWidth - 30, 20);
    
    yPos += 8;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('RISK PROFILE:', 20, yPos);
    doc.setFontSize(14);
    doc.text(profile.risk_profile.toUpperCase(), 60, yPos);
    doc.setFontSize(11);
    doc.text(`Total Score: ${profile.total_score}/100`, pageWidth - 60, yPos);
    yPos += 18;

    // Questions and Answers
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.text('Assessment Details', 20, yPos);
    yPos += 8;

    const answers = profile.answers;
    let questionNum = 1;

    Object.keys(answers).forEach((key) => {
      const answer = answers[key];
      
      // Check if we need a new page
      if (yPos > pageHeight - 60) {
        addFooter(1);
        doc.addPage();
        yPos = 20;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.setTextColor(30, 64, 175);
      
      const questionText = `${questionNum}. ${answer.question}`;
      const questionLines = doc.splitTextToSize(questionText, pageWidth - 40);
      doc.text(questionLines, 20, yPos);
      yPos += questionLines.length * 5;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(0, 0, 0);
      
      const answerText = `Answer: ${answer.answer} (Points: ${answer.points})`;
      const answerLines = doc.splitTextToSize(answerText, pageWidth - 40);
      doc.text(answerLines, 25, yPos);
      yPos += answerLines.length * 5 + 5;

      questionNum++;
    });

    // Risk Profile Interpretation
    if (yPos > pageHeight - 80) {
      addFooter(doc.internal.pages.length - 1);
      doc.addPage();
      yPos = 20;
    }

    yPos += 5;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text('Risk Profile Interpretation', 20, yPos);
    yPos += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    
    let interpretation = '';
    if (profile.risk_profile === 'Conservative') {
      interpretation = 'Conservative investors prefer capital preservation and steady returns. They are comfortable with lower returns in exchange for greater stability and security. Suitable investments include fixed deposits, debt funds, and low-risk instruments.';
    } else if (profile.risk_profile === 'Moderate') {
      interpretation = 'Moderate investors seek a balanced approach between growth and safety. They can tolerate moderate fluctuations for potentially higher returns. Suitable investments include balanced funds, hybrid funds, and a mix of equity and debt.';
    } else {
      interpretation = 'Aggressive investors prioritize high returns and are comfortable with significant market volatility. They have a long investment horizon and can withstand short-term losses. Suitable investments include equity funds, growth stocks, and high-risk instruments.';
    }

    const interpretationLines = doc.splitTextToSize(interpretation, pageWidth - 40);
    doc.text(interpretationLines, 20, yPos);
    yPos += interpretationLines.length * 5 + 10;

    // Disclaimer
    if (yPos > pageHeight - 70) {
      addFooter(doc.internal.pages.length - 1);
      doc.addPage();
      yPos = 20;
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(180, 0, 0);
    doc.text('Important Disclaimer', 20, yPos);
    yPos += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(80, 0, 0);
    const disclaimer = 'This risk profile assessment is for informational purposes only. It does not constitute investment advice. Investors should consult with a financial advisor before making any investment decisions. Past performance is not indicative of future returns. Mutual fund investments are subject to market risks. Read all scheme-related documents carefully before investing.';
    const disclaimerLines = doc.splitTextToSize(disclaimer, pageWidth - 40);
    doc.text(disclaimerLines, 20, yPos);
    yPos += disclaimerLines.length * 4 + 10;

    // Investor Declaration & Signature block
    if (yPos > pageHeight - 90) {
      addFooter(doc.internal.pages.length - 1);
      doc.addPage();
      yPos = 20;
    }
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.text('Investor Declaration & Signature', 20, yPos);
    yPos += 6;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    const declaration = 'I confirm that the answers provided above accurately reflect my current financial situation and risk appetite. I understand this profile will be used for investment recommendations.';
    const declLines = doc.splitTextToSize(declaration, pageWidth - 40);
    doc.text(declLines, 20, yPos);
    yPos += declLines.length * 4 + 4;

    // Place & Date on left; Signature image on right
    doc.setFontSize(9);
    doc.text(`Place: ${profile.signature_place || '________________________'}`, 20, yPos + 6);
    const sigDateStr = profile.signature_date
      ? new Date(profile.signature_date).toLocaleDateString('en-IN')
      : (profile.created_at ? new Date(profile.created_at).toLocaleDateString('en-IN') : '________________________');
    doc.text(`Date:  ${sigDateStr}`, 20, yPos + 14);

    const sigBoxX = pageWidth - 85;
    const sigBoxY = yPos;
    const sigBoxW = 70;
    const sigBoxH = 22;
    doc.setDrawColor(120, 120, 120);
    doc.setLineWidth(0.3);
    doc.rect(sigBoxX, sigBoxY, sigBoxW, sigBoxH);
    if (profile.signature_data_url) {
      try {
        doc.addImage(profile.signature_data_url, 'PNG', sigBoxX + 1, sigBoxY + 1, sigBoxW - 2, sigBoxH - 2);
      } catch (err) {
        console.error('Signature embed failed:', err);
      }
    }
    doc.setFontSize(7);
    doc.setTextColor(120, 120, 120);
    doc.text('Investor Signature', sigBoxX + sigBoxW / 2, sigBoxY + sigBoxH + 4, { align: 'center' });
    doc.setTextColor(0, 0, 0);

    // Add footer to all pages
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      addFooter(i);
    }

    // Save PDF
    doc.save(`Risk_Profile_${profile.investor_name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!profile) {
    return <div className="min-h-screen flex items-center justify-center">Profile not found</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 p-8">
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          {!publicToken ? (
            <Button
              variant="outline"
              onClick={() => navigate('/admin/risk-profiling')}
            >
              <ArrowLeft size={20} className="mr-2" />
              Back to Risk Profiles
            </Button>
          ) : <div />}
          <Button
            onClick={downloadPDF}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Download size={20} className="mr-2" />
            Download PDF
          </Button>
        </div>

        {/* Investor Information Card */}
        <Card className="mb-6">
          <CardHeader className="bg-blue-50">
            <CardTitle className="text-2xl flex items-center gap-2">
              <User size={24} />
              Investor Information
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-slate-600">Full Name</p>
                <p className="font-semibold text-slate-900 text-lg">{profile.investor_name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600">Age</p>
                <p className="font-semibold text-slate-900 text-lg">{profile.age} years</p>
              </div>
              <div>
                <p className="text-sm text-slate-600 flex items-center gap-2">
                  <Mail size={16} />
                  Email
                </p>
                <p className="font-semibold text-slate-900">{profile.investor_email}</p>
              </div>
              <div>
                <p className="text-sm text-slate-600 flex items-center gap-2">
                  <Phone size={16} />
                  Phone
                </p>
                <p className="font-semibold text-slate-900">{profile.investor_phone}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Risk Profile Result Card */}
        <Card className={`mb-6 border-2 ${getRiskColor(profile.risk_profile)}`}>
          <CardHeader>
            <CardTitle className="text-2xl flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Award size={28} />
                Risk Profile Assessment Result
              </span>
              <span className="text-3xl font-bold">{profile.risk_profile}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Total Score</p>
                <p className="text-4xl font-bold">{profile.total_score}</p>
                <p className="text-sm text-slate-600">out of 100 points</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-600">Assessment Date</p>
                <p className="font-semibold">{new Date(profile.created_at).toLocaleDateString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Questions and Answers */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Assessment Details - Questions & Answers</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {Object.keys(profile.answers).map((key, index) => {
              const answer = profile.answers[key];
              return (
                <div key={key} className="border-l-4 border-blue-500 pl-4 py-2 bg-slate-50 rounded">
                  <p className="font-semibold text-slate-900 mb-2">
                    {index + 1}. {answer.question}
                  </p>
                  <p className="text-slate-700">
                    <span className="font-medium">Answer:</span> {answer.answer}
                  </p>
                  <p className="text-sm text-blue-600 font-semibold mt-1">
                    Points Awarded: {answer.points}
                  </p>
                </div>
              );
            })}
          </CardContent>
        </Card>

        {/* Risk Profile Interpretation */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Risk Profile Interpretation</CardTitle>
          </CardHeader>
          <CardContent>
            {profile.risk_profile === 'Conservative' && (
              <p className="text-slate-700 leading-relaxed">
                <strong>Conservative investors</strong> prefer capital preservation and steady returns. They are comfortable with lower returns in exchange for greater stability and security. Suitable investments include fixed deposits, debt funds, government bonds, and low-risk instruments.
              </p>
            )}
            {profile.risk_profile === 'Moderate' && (
              <p className="text-slate-700 leading-relaxed">
                <strong>Moderate investors</strong> seek a balanced approach between growth and safety. They can tolerate moderate fluctuations for potentially higher returns. Suitable investments include balanced funds, hybrid funds, and a diversified mix of equity and debt instruments.
              </p>
            )}
            {profile.risk_profile === 'Aggressive' && (
              <p className="text-slate-700 leading-relaxed">
                <strong>Aggressive investors</strong> prioritize high returns and are comfortable with significant market volatility. They have a long investment horizon and can withstand short-term losses. Suitable investments include equity funds, growth stocks, mid-cap and small-cap funds, and high-risk instruments.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Investor Signature */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Investor Declaration & Signature</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-700 mb-4">
              I confirm that the answers provided above accurately reflect my current financial situation
              and risk appetite. I understand this profile will be used for investment recommendations.
            </p>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <p className="text-xs text-slate-500">Place</p>
                <p className="font-semibold text-slate-900" data-testid="view-signature-place">
                  {profile.signature_place || '—'}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Date</p>
                <p className="font-semibold text-slate-900" data-testid="view-signature-date">
                  {profile.signature_date
                    ? new Date(profile.signature_date).toLocaleDateString('en-IN')
                    : new Date(profile.created_at).toLocaleDateString('en-IN')}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Signature</p>
                <div
                  className="mt-1 border border-slate-300 rounded bg-white h-24 flex items-center justify-center overflow-hidden"
                  data-testid="view-signature-box"
                >
                  {profile.signature_data_url ? (
                    <img
                      src={profile.signature_data_url}
                      alt="Investor signature"
                      className="max-h-24 max-w-full object-contain"
                    />
                  ) : (
                    <span className="text-xs text-slate-400 italic">Not signed online (offline copy)</span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Disclaimer */}
        <div className="mt-6 bg-red-50 border-l-4 border-red-500 p-4 rounded">
          <p className="font-bold text-red-900 mb-2">Important Disclaimer</p>
          <p className="text-sm text-red-800">
            This risk profile assessment is for informational purposes only. It does not constitute investment advice. Investors should consult with a qualified financial advisor before making any investment decisions. Past performance is not indicative of future returns. Mutual fund investments are subject to market risks. Read all scheme-related documents carefully before investing.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ViewRiskProfile;
